import { MsgType } from 'matrix-js-sdk';
import { useCallback } from 'react';
import { useMatrixContext } from '../MatrixContext';

export interface MessageContent {
  body: string;
  formatted_body?: string;
  msgType?: MsgType;
}

export interface MessageCommandsAPI {
  sendMessage: (roomId: string, body: string, formattedBody?: string) => Promise<string>;
  sendJsonMessage: (roomId: string, jsonData: object, formattedBody?: string) => Promise<string>;
  markAsRead: (roomId?: string) => Promise<void>;
  markAllAsRead: (options: { includeRoomTypes?: string[]; excludeRoomTypes?: string[] }) => Promise<void>;
  redactMessage: (roomId: string, eventId: string, reason?: string) => Promise<void>;
}

export function useMessageCommands(): MessageCommandsAPI {
  const { client } = useMatrixContext();

  const sendMessage = useCallback(
    async (roomId: string, body: string, formattedBody?: string): Promise<string> => {
      if (!client) {
        throw new Error('useMessageCommands must be used within MatrixProvider');
      }
      const response = await client.sendMessage(roomId, {
        body,
        formatted_body: formattedBody,
        msgtype: MsgType.Text,
      });
      return response.event_id;
    },
    [client],
  );

  const sendJsonMessage = useCallback(
    async (roomId: string, jsonData: object, formattedBody?: string): Promise<string> => {
      if (!client) {
        throw new Error('useMessageCommands must be used within MatrixProvider');
      }
      const response = await client.sendMessage(roomId, {
        body: JSON.stringify(jsonData),
        formatted_body: formattedBody,
        msgtype: MsgType.Text,
      });
      return response.event_id;
    },
    [client],
  );

  const markAsRead = useCallback(
    async (roomId?: string): Promise<void> => {
      if (!client) {
        throw new Error('useMessageCommands must be used within MatrixProvider');
      }
      if (!roomId) return;
      const room = client.getRoom(roomId);
      if (!room) return;

      const timeline = room.getLiveTimeline();
      const events = timeline.getEvents();
      if (events.length > 0) {
        const lastEvent = events[events.length - 1];
        if (lastEvent) {
          const eventId = lastEvent.getId();
          if (eventId) {
            await client.setRoomReadMarkers(roomId, eventId);
            await client.sendReadReceipt(lastEvent);
          }
        }
      }
    },
    [client],
  );

  const markAllAsRead = useCallback(
    async (options: { includeRoomTypes?: string[]; excludeRoomTypes?: string[] }): Promise<void> => {
      if (!client) {
        throw new Error('useMessageCommands must be used within MatrixProvider');
      }
      const rooms = client.getVisibleRooms().filter((room) => {
        const roomType = room.name.split('_')[0];
        return (
          !(
            options.includeRoomTypes &&
            options.includeRoomTypes.length > 0 &&
            !options.includeRoomTypes.includes(roomType)
          ) &&
          !(
            options.excludeRoomTypes &&
            options.excludeRoomTypes.length > 0 &&
            options.excludeRoomTypes.includes(roomType)
          )
        );
      });

      await Promise.all(
        rooms.map(async (room) => {
          const timeline = room.getLiveTimeline();
          const events = timeline.getEvents();
          if (events.length > 0) {
            const lastEvent = events[events.length - 1];
            if (lastEvent) {
              const eventId = lastEvent.getId();
              if (eventId) {
                await client.setRoomReadMarkers(room.roomId, eventId);
                await client.sendReadReceipt(lastEvent);
              }
            }
          }
        }),
      );
    },
    [client],
  );

  const redactMessage = useCallback(
    async (roomId: string, eventId: string, reason?: string): Promise<void> => {
      if (!client) {
        throw new Error('useMessageCommands must be used within MatrixProvider');
      }
      await client.redactEvent(roomId, eventId, undefined, reason ? { reason } : undefined);
    },
    [client],
  );

  return {
    sendMessage,
    sendJsonMessage,
    markAsRead,
    markAllAsRead,
    redactMessage,
  };
}
